import React from "react";
import Facts from "components/Facts";

import map from "../map";
import d3RedRiverLogo from "components/d3RedRiverLogo";
import { animations, previousTimeStamp, frameProcs } from "../animation";
import { createMeter } from "../meter";
import logLevels from "./logLevels";

import { pendingEventStats } from "./role";
import { mapDefs } from "../render";

const numObjects = (type) =>
  Object.values(map.objects).filter((o) => o.type == type).length;

const area = 1500; // 439
const dotRadius = 4;
const radius = Math.sqrt(area / Math.PI);

export default {
  layout: { radius: (radius + 1) * dotRadius * 2 },
  render: {
    enter: (o) => {
      //d3RedRiverLogo(o.elem);

      const mainRad = (radius + 1) * dotRadius * 2;
      const innerRad = (radius * 9) / 16;
      const innerRadPix = (innerRad - 1) * dotRadius * 2;

      const clipPathName = "borgClipPath";
      const svgDefs = mapDefs();
      const pathSel = svgDefs.selectAll("#" + clipPathName);
      if (pathSel.length == 0) {
        svgDefs
          .append("clipPath")
          .attr("id", clipPathName)
          .append("circle")
          .attr("r", innerRadPix);
      }

      o.elem.append("circle").attr("fill", "white").attr("r", mainRad);

      o.elem
        .append("line")
        .attr("x1", 0)
        .attr("y1", -innerRadPix)
        .attr("x2", 0)
        .attr("y2", innerRadPix)
        .attr("stroke", "#faa");

      const graphGrp = o.elem.append("g");
      graphGrp.attr("clip-path", "url(#" + clipPathName + ")");

      let g = graphGrp.append("g");
      g.attr("transform", "translate(0,-1)");
      var memMeter = createMeter(
        g,
        1024 * 1024 * 1024,
        100 + innerRadPix * 2,
        (v) => (v / 1024 / 1024 / 1024).toFixed(0) + "GB",
        0,
        -1
      );

      g = graphGrp.append("g");
      g.attr("transform", "translate(0,1)");
      var cpuMeter = createMeter(
        g,
        1,
        100 + innerRadPix * 2,
        (v) => (v == 1 ? "1 core" : v + " cores"),
        0,
        1
      );

      o.elem
        .append("circle")
        .attr("r", innerRadPix)
        .attr("stroke", "#ccc")
        .attr("fill", "none");

      let pts = [];

      let ofs = 1;

      const cramp = Math.sqrt(3) / 2;

      // How far the arm leans over the width of the ring, as a fraction of a turn.
      // Higher values coil it more tightly; at zero it degenerates into a spoke.
      const TWO_PI = Math.PI * 2;
      const swirlTurns = 0.375;
      const swirlPerUnitRadius = (TWO_PI * swirlTurns) / (radius - innerRad);

      for (
        let y = -radius - 1 - cramp / 2;
        y <= radius + 1 + cramp / 2;
        y += cramp
      ) {
        ofs = ofs == 1 ? 1.5 : 1;
        for (let x = -radius - ofs; x <= radius + ofs; x++) {
          // Dots are ordered by their position along a rotating spiral arm, and the
          // chaser then walks that order cyclically.
          //
          // The arm is the angle skewed by radius, which is what makes it a swirl
          // rather than a sweeping spoke. Taking that modulo a full turn is what
          // makes it seamless: the key stays bounded, so the end of the array meets
          // the start at the same point on the arm. Previously the radius was added
          // unbounded, so the wrap jumped from the outer edge back to the inner one
          // and drew a line across the dial - and atan2's own (-PI, PI] wrap put
          // that line due west.
          const r = Math.sqrt(x * x + y * y) || 0;
          let phase = (Math.atan2(y, x) + swirlPerUnitRadius * r) % TWO_PI;
          if (phase < 0) phase += TWO_PI;
          if (r > innerRad && r <= radius) {
            pts.push({
              x: x,
              y: y,
              order: [phase, 0],
            });
          }
        }
      }

      pts.sort((a, b) => a.order[0] - b.order[0]);
      pts.sort((b, a) => a.order[1] - b.order[1]);

      for (var i = 0; i < pts.length; i++) {
        pts[i].elem = o.elem
          .append("circle")
          .attr("cx", pts[i].x * dotRadius * 2)
          .attr("cy", pts[i].y * dotRadius * 2)
          .attr("r", dotRadius - 0.5)
          .attr("fill", "none")
          .attr("stroke", "none");
      }

      let nextPt = 0;
      let nextOldPt = Math.floor(pts.length * 0.5);

      // A dot going out used to snap to the background colour, which read as a
      // flicker against the ones still lighting up. Fading it lets the swirl
      // trail off behind the leading edge instead.
      const dotFadeMs = 400;
      const dotOff = "#f7f7f7";
      const dotOffRgb = [247, 247, 247];

      function rgbOf(hex) {
        const h = hex.slice(1);
        const wide = h.length >= 6;
        const at = (i) =>
          wide
            ? parseInt(h.slice(i * 2, i * 2 + 2), 16)
            : parseInt(h[i] + h[i], 16);
        return [at(0), at(1), at(2)];
      }

      function mix(from, to, v) {
        const c = (i) => Math.round(from[i] + (to[i] - from[i]) * v);
        return `rgb(${c(0)},${c(1)},${c(2)})`;
      }

      // Bumped whenever a dot is written to, so a fade that is still running on a
      // dot which has since been lit again stops rather than dimming underneath
      // the new colour.
      function claim(pt) {
        return (pt.generation = (pt.generation || 0) + 1);
      }

      function fadeOut(pt) {
        // Dots that have never been lit start out unpainted; there is nothing to
        // fade from, so they just take the background.
        if (!pt.colour) {
          claim(pt);
          pt.elem.attr("fill", dotOff);
          return;
        }

        const from = rgbOf(pt.colour);
        const generation = claim(pt);
        pt.colour = null;

        animations.add({
          type: "fade-level-dot",
          // As the page-switch animation does: without an owner, a dial that is
          // rebuilt leaves its fades running for the lifetime of the page.
          owner: o.elem,
          start: previousTimeStamp,
          from: 0,
          to: 1,
          duration: dotFadeMs,
          frame: (v, done) => {
            if (pt.generation !== generation) return;
            pt.elem.attr("fill", done ? dotOff : mix(from, dotOffRgb, v));
          },
        });
      }

      function addPt(c, persist) {
        const pt = pts[nextPt];
        claim(pt);
        pt.colour = c;
        pt.elem.attr("fill", c);
        nextPt = (nextPt + 1) % pts.length;

        fadeOut(pts[nextOldPt]);
        nextOldPt = (nextOldPt + 1) % pts.length;
      }

      let lastCpuTotal = 0;
      let lastMemTotal = 0;

      frameProcs.borg = (t) => {
        let cpuTotal = 0;
        let memTotal = 0;
        for (const obj of Object.values(map.objects)) {
          if (obj.type == "process" && obj.cpuCores && obj.memBytes) {
            cpuTotal += obj.cpuCores;
            memTotal += obj.memBytes;
          }
        }

        lastCpuTotal = lastCpuTotal * (3 / 4) + cpuTotal * (1 / 4);
        lastMemTotal = lastMemTotal * (3 / 4) + memTotal * (1 / 4);

        cpuMeter.setValue(lastCpuTotal);
        memMeter.setValue(lastMemTotal);

        const byLevel = [0, 0, 0, 0, 0, 0];
        for (var i = 0; i < pendingEventStats.length; i++) {
          for (var j = 0; j < pendingEventStats[i].length; j++) {
            byLevel[j] += pendingEventStats[i][j];
          }
        }

        for (var i = 0; i < byLevel.length; i++) {
          if (byLevel[i] == 0) continue;
          const persist = i > 2;
          const colour = logLevels[i].colour;
          let added = 0;
          animations.add({
            type: "add-level-dots",
            start: previousTimeStamp,
            from: 0,
            to: byLevel[i],
            duration: 1000,
            frame: (v, done) => {
              const totNow = Math.floor(v);
              const toAdd = totNow - added;
              for (var k = 0; k < toAdd; k++) {
                addPt(colour, persist);
              }
              added += toAdd;
            },
          });
        }

        pendingEventStats.length = 0;
      };
    },
  },

  renderInfoHead: (t) => "Root",
  renderInfoBody: (t) => (
    <Facts>
      <Facts.Fact label="Apps" value={numObjects("app")} />
      <Facts.Fact label="Machines" value={numObjects("machine")} />
      <Facts.Fact label="Processes" value={numObjects("process")} />
      <Facts.Fact label="Saffron versions" value={getSaffVersions(t)} />
    </Facts>
  ),
};

function getSaffVersions(t) {
  return (
    Object.values(map.objects)
      .filter((o) => o.type == "process" && o.data)
      .map((o) => JSON.parse(o.data).saffronVersion)
      .filter((v) => v)
      .reduce(
        (p, c) => (p == "" ? c : p.indexOf(c) == -1 ? p + ", " + c : p),
        ""
      ) || "Unknown"
  );
}
